import React, { createContext, useState, useEffect, useCallback } from 'react';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('token') || null);
  const [userInfo, setUserInfo] = useState(() => {
    const storedUser = localStorage.getItem('userInfo');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const handleLogout = useCallback(() => {
    setToken(null);
    setUserInfo(null);
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
  }, []);

  const fetchUserInfo = useCallback(async (authToken) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) {
        handleLogout();
        return;
      }
      const data = await res.json();
      setUserInfo(data);
      localStorage.setItem('userInfo', JSON.stringify(data));
      return data;
    } catch (err) {
      handleLogout();
    }
  }, [handleLogout]);

  const handleLogin = useCallback(
    async (idToken, devEmail = null, devName = null) => {  // Accept devName
      if (process.env.REACT_APP_MOCK_LOGIN === 'true' && devEmail) {
        try {
          const resp = await fetch(`${process.env.REACT_APP_API_URL}/dev-login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: devEmail, name: devName }), // Send name
          });
  
          if (!resp.ok) {
            const errorData = await resp.json();
            console.error('Dev login failed:', errorData.error);
            handleLogout();
            throw new Error(errorData.error || 'Dev login failed');
          }
  
          const data = await resp.json();
          const devToken = data.token;
  
          setToken(devToken);
          localStorage.setItem('token', devToken);
  
          await fetchUserInfo(devToken);
        } catch (err) {
          console.error('Error during dev login:', err);
          handleLogout();
          throw err;
        }
        return;
      }
  
      // Normal Google login flow
      setToken(idToken);
      localStorage.setItem('token', idToken);
  
      try {
        const resp = await fetch(`${process.env.REACT_APP_API_URL}/me`, {
          headers: { 'Authorization': `Bearer ${idToken}` },
        });
  
        if (resp.ok) {
          const data = await resp.json();
          setUserInfo(data);
          localStorage.setItem('userInfo', JSON.stringify(data));
        } else {
          console.error('Failed to fetch user info');
          handleLogout();
        }
      } catch (err) {
        console.error(err);
        handleLogout();
      }
    },
    [fetchUserInfo, handleLogout]
  );

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        await fetchUserInfo(token);
      }
    };
    verifyToken();
  }, [token, fetchUserInfo]);

  const value = { token, userInfo, setUserInfo, handleLogin, handleLogout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
